import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, CalendarDays, Check, Copy, TicketPercent } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "../lib/api";
import { LanguageProvider, pick, setSiteOverrides, useLang } from "../lib/i18n";
import { buildBookingUrl, goBooking, formatDateRange, parseLines, scheduleStatus } from "../lib/promo";
import { LogoMark } from "../components/Logo";
import Seo from "../components/Seo";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import CaptionedImage from "../components/CaptionedImage";

const statusStyles = {
  active: ["Active", "bg-[#E8F0EC] text-[#2D5A3F]"],
  upcoming: ["Upcoming", "bg-[#F8F3E8] text-[#8A622A]"],
  expired: ["Expired", "bg-[#F7ECEC] text-[#7A3E3E]"],
};

function PromoDetailInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [site, setSite] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get("/site")
      .then((r) => {
        setSiteOverrides(r.data.content || {});
        setSite(r.data);
      })
      .catch(() => setSite({ error: true }));
  }, []);

  if (!site) {
    return (
      <div data-testid="promo-detail-loader" className="min-h-screen flex items-center justify-center bg-[#FBF9F5]">
        <LogoMark className="w-12 h-12 animate-pulse" color="#9A7B66" />
      </div>
    );
  }

  const offer = site.offers?.find((item) => item.id === id || item.slug === id);
  if (!offer) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FBF9F5] gap-4 px-5 text-center">
        <p data-testid="promo-not-found" className="text-[#6E675F] font-sans">Promotion not found.</p>
        <Link data-testid="promo-not-found-back" to="/" className="text-[#9A7B66] underline underline-offset-4 text-sm font-sans">
          Back to offers
        </Link>
      </div>
    );
  }

  if (offer.slug && id !== offer.slug) {
    return <Navigate to={`/offer/${offer.slug}`} replace />;
  }

  const settings = site.settings || {};
  const status = scheduleStatus(offer.start_date, offer.end_date);
  const [statusLabel, statusClass] = statusStyles[status];
  const inclusions = parseLines(offer.inclusions);
  const terms = parseLines(offer.terms);
  const bookingUrl = buildBookingUrl(offer.booking_url || settings.booking_engine_url, offer.promo_code || "");
  const relatedOffers = (site.offers || []).filter((item) => item.id !== offer.id).slice(0, 3);

  const copyPromoCode = async () => {
    if (!offer.promo_code) return;
    try {
      await navigator.clipboard.writeText(offer.promo_code);
      toast.success("Promo code copied");
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = offer.promo_code;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand("copy");
      textarea.remove();
      if (copied) toast.success("Promo code copied");
      else toast.error("Could not copy the promo code");
    }
  };

  const openBooking = () => {
    // Opens the embedded /book page with the promo code carried into the Exely engine
    if (status === "expired") return;
    const target = buildBookingUrl(offer.booking_url || bookingUrl, offer.promo_code || "");
    if (target) goBooking(target);
  };

  return (
    <div data-testid="promo-detail-page" className="bg-[#FBF9F5] min-h-screen">
      <Seo
        title={`${pick(offer, "title", lang)} | Nusadira Lembongan Villa Offer`}
        description={`${pick(offer, "title", lang)} at Nusadira Lembongan Villa. ${pick(offer, "description", lang)}`}
        image={mediaUrl(offer.image_url)}
        imageAlt={offer.image_alt || pick(offer, "title", lang)}
      />
      <Navbar
        bookingUrl={settings.booking_engine_url}
        logoUrl={settings.logo_url}
        logoLightUrl={settings.logo_light_url}
        navMenu={settings.nav_menu}
        hiddenSections={String(settings.hidden_sections || "").split(",").map((s) => s.trim()).filter(Boolean)}
      />

      <div className="relative h-[55svh] min-h-[420px] overflow-hidden bg-[#1E1A17]">
        <motion.div
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full h-full"
        >
          <CaptionedImage
            testId="promo-hero-image"
            src={mediaUrl(offer.image_url)}
            alt={offer.image_alt || pick(offer, "title", lang)}
            title={offer.image_title || pick(offer, "title", lang)}
            description={offer.image_description || pick(offer, "description", lang)}
            caption={offer.image_caption || ""}
            focalPoint={offer.image_focal_point || "50% 0%"}
            loading="eager"
            className="w-full h-full"
            imageClassName="w-full h-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E1A17]/65 via-[#1E1A17]/10 to-[#1E1A17]/20" />
      </div>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.section initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75 }} className="lg:col-span-7">
            <div className="flex flex-wrap items-center gap-4 mb-10">
              <Link
                data-testid="promo-back-link"
                to="/"
                className="inline-flex items-center gap-2 text-[#9A7B66] hover:text-[#826552] text-[12px] font-sans font-semibold uppercase tracking-[0.16em] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> All Offers
              </Link>
              <span
                data-testid="promo-status-badge"
                className={`rounded-full px-4 py-1.5 text-[11px] font-sans font-semibold uppercase tracking-[0.16em] ${statusClass}`}
              >
                {statusLabel}
              </span>
            </div>
            <p className="text-xs uppercase tracking-[0.25em] font-sans font-medium text-[#9A7B66] mb-4">
              {offer.tag || t("offers.eyebrow")}
            </p>
            <h1 data-testid="promo-title" className="font-serif font-normal tracking-tight text-[#2C2825] leading-tight text-4xl sm:text-5xl lg:text-6xl">
              {pick(offer, "title", lang)}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#6E675F] font-sans font-light leading-relaxed max-w-2xl">
              {pick(offer, "description", lang)}
            </p>

            <div data-testid="promo-validity-dates" className="mt-10 bg-white border border-[#E5DED4] rounded-md p-6 flex flex-col sm:flex-row gap-6">
              <div className="flex items-start gap-3">
                <CalendarDays className="w-5 h-5 text-[#9A7B66] mt-0.5" />
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#A0988E] font-sans font-semibold">Promotion Period</p>
                  <p className="mt-1 font-serif text-xl text-[#2C2825]">{formatDateRange(offer.start_date, offer.end_date)}</p>
                </div>
              </div>
              {offer.minimum_nights && (
                <div className="sm:ml-auto sm:text-right">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#A0988E] font-sans font-semibold">Minimum Stay</p>
                  <p className="mt-1 font-serif text-xl text-[#2C2825]">{offer.minimum_nights} nights</p>
                </div>
              )}
            </div>

            <section className="mt-14">
              <h2 className="font-serif text-3xl text-[#2C2825] mb-7">What Is Included</h2>
              {inclusions.length ? (
                <div data-testid="promo-inclusions-list" className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                  {inclusions.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-[#9A7B66] mt-1 shrink-0" />
                      <span className="text-sm text-[#6E675F] font-sans leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#A0988E] font-sans">Our concierge will confirm the full inclusions before booking.</p>
              )}
            </section>

            <section data-testid="promo-terms-section" className="mt-14 border-t border-[#E5DED4] pt-10">
              <h2 className="font-serif text-3xl text-[#2C2825] mb-7">Terms & Conditions</h2>
              {terms.length ? (
                <ul className="space-y-4">
                  {terms.map((item, index) => (
                    <li key={index} className="flex items-start gap-4 text-sm text-[#6E675F] font-sans leading-relaxed">
                      <span className="font-mono text-xs text-[#9A7B66] mt-0.5">{String(index + 1).padStart(2, "0")}</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[#A0988E] font-sans">Terms will be shared by our concierge before confirmation.</p>
              )}
            </section>
          </motion.section>

          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.12 }}
            className="lg:col-span-5"
          >
            <div className="lg:sticky lg:top-28 bg-[#1E1A17] text-[#F4EFEA] rounded-md p-7 sm:p-8 shadow-[0_24px_70px_rgba(30,26,23,0.18)]">
              <p className="text-[11px] uppercase tracking-[0.22em] font-sans font-semibold text-[#D8CBB8]/70">Direct Booking Privilege</p>
              <h2 className="mt-3 font-serif text-3xl leading-tight">Reserve this island offer</h2>

              {offer.promo_code && (
                <div data-testid="promo-code-card" className="mt-7 rounded-md border border-white/10 bg-white/5 p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#D8CBB8]/60 font-sans font-semibold mb-3">Promo Code</p>
                  <div className="flex items-center justify-between gap-4">
                    <span className="inline-flex items-center gap-2 font-mono text-lg tracking-[0.18em] text-white">
                      <TicketPercent className="w-5 h-5 text-[#D8CBB8]" /> {offer.promo_code}
                    </span>
                    <button
                      data-testid="promo-code-copy-btn"
                      onClick={copyPromoCode}
                      aria-label="Copy promo code"
                      className="p-2.5 rounded-sm bg-white/10 hover:bg-[#9A7B66] text-white transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              <button
                data-testid="promo-book-now-button"
                onClick={openBooking}
                disabled={status === "expired"}
                className="mt-7 w-full group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-sm bg-[#9A7B66] hover:bg-[#826552] disabled:opacity-45 disabled:hover:bg-[#9A7B66] text-white text-[12px] font-sans font-semibold uppercase tracking-[0.16em] transition-colors"
              >
                {status === "expired" ? "Promotion Ended" : status === "upcoming" ? "Book Ahead" : t("offers.book")}
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <p className="mt-4 text-xs text-[#D8CBB8]/65 font-sans leading-relaxed">
                The booking engine opens in a new tab with the promotion code applied whenever available.
              </p>
            </div>
          </motion.aside>
        </div>

        {relatedOffers.length > 0 && (
          <section data-testid="related-offers-section" className="mt-20 border-t border-[#E5DED4] pt-12">
            <h2 className="font-serif text-3xl text-[#2C2825] mb-8">More Island Privileges</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedOffers.map((item, index) => (
                <Link
                  key={item.id || index}
                  data-testid={`related-offer-${index}`}
                  to={`/offer/${item.slug || item.id}`}
                  className="group bg-white border border-[#E5DED4] rounded-md overflow-hidden hover:border-[#9A7B66] transition-colors"
                >
                  <div className="h-44 overflow-hidden">
                    <CaptionedImage
                      testId={`related-offer-image-${index}`}
                      src={mediaUrl(item.image_url)}
                      alt={item.image_alt || pick(item, "title", lang)}
                      title={item.image_title || pick(item, "title", lang)}
                      description={item.image_description || pick(item, "description", lang)}
                      caption={item.image_caption || ""}
                      focalPoint={item.image_focal_point || "50% 0%"}
                      className="w-full h-full"
                      imageClassName="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#9A7B66] font-sans font-semibold">{item.tag}</p>
                    <h3 className="mt-2 font-serif text-xl text-[#2C2825] leading-snug">{pick(item, "title", lang)}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer settings={settings} />
    </div>
  );
}

export default function PromoDetail() {
  return (
    <LanguageProvider>
      <PromoDetailInner />
    </LanguageProvider>
  );
}
